import { useCallback, useEffect, useState, type ReactNode } from "react";
import { useParams, useSearchParams } from "react-router";
import { refreshEvent, site } from "./current";
import { setDefaultMessages } from "./messages";
import { DAY_MS, dayKey, visitorsOf } from "./lib/analytics";
import { parseFilters, periods, reportFrom } from "./lib/stats-report-core";
import { firstDay, loadEvents } from "./stats";
import type { FaqGroupRecord, LeadRecord, MediaItem, MessageOverrides, Pages, PostRecord, ProjectRecord, ScoreRecord, ServiceRecord, VersionInfo } from "./types";
import { AdminLoading } from "./admin/AdminLoading";
import { FaqEditor } from "./admin/FaqEditor";
import { HistoryBrowser } from "./admin/HistoryBrowser";
import { InterfaceTextEditor } from "./admin/InterfaceTextEditor";
import { LaunchSwitch } from "./admin/LaunchSwitch";
import { LeadsInbox } from "./admin/LeadsInbox";
import { MediaLibrary } from "./admin/MediaLibrary";
import { PagesEditor } from "./admin/PagesEditor";
import { PostEditor } from "./admin/PostEditor";
import { ProjectEditor } from "./admin/ProjectEditor";
import { ScoresAdmin } from "./admin/ScoresAdmin";
import { ServiceEditor } from "./admin/ServiceEditor";
import { SortableList } from "./admin/SortableList";
import { StatsFilters } from "./admin/stats/StatsFilters";
import { Chart, Empty, Kpis, Panel, RankTable, Vitals, filterHref, fmtDuration } from "./admin/stats/StatsPanels";
import { BTN_PRIMARY, CARD, PageTitle } from "./admin/ui";
import { deletePost, deleteProject, deleteService, reorderPosts, reorderProjects, reorderServices } from "./actions";
import Link from "./link";
import { PublishPanel } from "./PublishPanel";

// The TASMA dashboard's pages in the hub: each loads its data from the website's admin API, then shows the screen the
// website's own dashboard showed (copied in admin/). Same order and wording as tasma-website app/admin/(panel)/*.

/** Data for a page: loaded on open, and again after a change elsewhere asks for it (refresh() in current.ts). */
function useLoad<T>(load: () => Promise<T>, key: string): { data?: T; error?: string } {
  const [state, setState] = useState<{ key: string; data?: T; error?: string }>({ key: "" });
  const run = useCallback(() => {
    let live = true;
    load().then(
      (data) => live && setState({ key, data }),
      (error: unknown) => live && setState({ key, error: error instanceof Error ? error.message : "Couldn't load this page." }),
    );
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  useEffect(() => {
    const stop = run();
    const again = () => run();
    window.addEventListener(refreshEvent, again);
    return () => {
      stop();
      window.removeEventListener(refreshEvent, again);
    };
  }, [run]);
  return state.key === key ? state : {};
}

const collection = async <T,>(name: string) => (await site().get<{ data: T }>(`/collections/${name}`)).data;

function Loaded<T>({ state, children }: { state: { data?: T; error?: string }; children: (data: T) => ReactNode }) {
  if (state.error) return <p className="rounded-2xl border border-red-700/30 bg-red-50 p-5 text-red-900">{state.error}</p>;
  if (state.data === undefined) return <AdminLoading />;
  return <>{children(state.data)}</>;
}

const count = (list: { status: string }[]) => {
  const live = list.filter((x) => x.status === "published").length;
  const drafts = list.length - live;
  return `${live} live${drafts ? `, ${drafts} draft${drafts > 1 ? "s" : ""}` : ""}`;
};

// ---------- overview ----------

export function OverviewPage({ can }: { can: (area: "content" | "media" | "leads" | "stats" | "settings") => boolean }) {
  const state = useLoad(async () => {
    const [posts, projects, services, faq, media, leads, week] = await Promise.all([
      can("content") ? collection<PostRecord[]>("posts") : [],
      can("content") ? collection<ProjectRecord[]>("projects") : [],
      can("content") ? collection<ServiceRecord[]>("services") : [],
      can("content") ? collection<FaqGroupRecord[]>("faq") : [],
      can("media") ? collection<MediaItem[]>("media") : [],
      can("leads") ? site().get<{ leads: LeadRecord[] }>("/leads").then((r) => r.leads) : [],
      can("stats") ? loadEvents(dayKey(Date.now() - 6 * DAY_MS), dayKey(Date.now())).catch(() => []) : [],
    ]);
    return { posts, projects, services, faq, media, leads, week };
  }, "overview");
  return (
    <Loaded state={state}>
      {({ posts, projects, services, faq, media, leads, week }) => {
        const fresh = leads.filter((l) => l.status === "new").length;
        const cards = [
          can("settings") && { href: "/admin/publish", title: "Publish", detail: "Put your saved changes on the website (takes 3–5 minutes)" },
          can("stats") && { href: "/admin/stats", title: "Statistics", detail: `${visitorsOf(week)} visitors in the last 7 days` },
          can("leads") && { href: "/admin/leads", title: fresh ? `Leads (${fresh} new)` : "Leads", detail: fresh ? `${fresh} message${fresh > 1 ? "s" : ""} waiting for a reply` : "No new messages" },
          can("content") && { href: "/admin/blog", title: "Blog", detail: count(posts), action: { href: "/admin/blog/new", label: "New post" } },
          can("content") && { href: "/admin/projects", title: "Projects", detail: count(projects), action: { href: "/admin/projects/new", label: "New project" } },
          can("content") && { href: "/admin/services", title: "Services", detail: count(services), action: { href: "/admin/services/new", label: "New service" } },
          can("content") && { href: "/admin/faq", title: "FAQ", detail: `${faq.reduce((n, g) => n + g.items.length, 0)} questions in ${faq.length} groups` },
          can("content") && { href: "/admin/pages", title: "Page text", detail: "Home, About and Contact, in English and Arabic" },
          can("content") && { href: "/admin/interface", title: "Interface text", detail: "Buttons, menus, labels and Google titles, in English and Arabic" },
          can("settings") && { href: "/admin/settings", title: "Settings", detail: "Contact details, social links, contact form, coming soon" },
          can("media") && { href: "/admin/media", title: "Media", detail: `${media.length} file${media.length === 1 ? "" : "s"}` },
        ].filter((c): c is { href: string; title: string; detail: string; action?: { href: string; label: string } } => Boolean(c));
        return (
          <div>
            <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">Welcome back</h1>
            <p className="mt-2 text-midnight/70">Changes are saved to the website straight away; press Publish to put them on the public pages. Drafts stay hidden.</p>
            <ul className="mt-8 grid gap-4 sm:grid-cols-2">
              {cards.map((c) => (
                <li key={c.href} className={`${CARD} flex flex-col gap-3`}>
                  <Link href={c.href} className="text-xl font-bold hover:text-teal">
                    {c.title} →
                  </Link>
                  <p className="text-sm text-midnight/70">{c.detail}</p>
                  {c.action && (
                    <Link href={c.action.href} className="mt-auto inline-flex min-h-11 w-fit items-center rounded-lg bg-midnight px-4 font-mono text-[0.7rem] tracking-[0.15em] text-offwhite uppercase hover:bg-teal">
                      + {c.action.label}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>
        );
      }}
    </Loaded>
  );
}

// ---------- statistics ----------

const CLICK_NAMES: Record<string, string> = { "get-in-touch": "Get in touch", instagram: "Instagram", email: "Email" };
const CHARACTER_NAMES: Record<string, string> = { wave: "Waved at the character", "tap-character": "Tapped the character", "sound-on": "Turned sound on", "sound-off": "Turned sound off" };
const characterName = (k: string) => CHARACTER_NAMES[k] ?? (k.startsWith("face-") ? `Changed expression: ${k.slice(5)}` : k);

export function StatsPage({ canLeads }: { canLeads: boolean }) {
  const [params] = useSearchParams();
  const filters = parseFilters(Object.fromEntries(params));
  const state = useLoad(async () => {
    const { now, before } = periods(filters);
    const [allNow, allBefore, posts, services, projects, leads, since] = await Promise.all([
      loadEvents(now.from, now.to),
      loadEvents(before.from, before.to),
      collection<PostRecord[]>("posts").catch(() => []),
      collection<ServiceRecord[]>("services").catch(() => []),
      collection<ProjectRecord[]>("projects").catch(() => []),
      canLeads ? site().get<{ leads: LeadRecord[] }>("/leads").then((r) => r.leads) : [],
      firstDay(),
    ]);
    return reportFrom(filters, { allNow, allBefore, posts, services, projects, leads, since });
  }, `stats:${params.toString()}`);
  return (
    <div>
      <PageTitle title="Statistics" />
      <Loaded state={state}>
        {(r) => {
          const share = (part: number, whole: number) => (whole ? `${Math.round((part / whole) * 100)}%` : "–");
          const totalVisitors = r.devices.reduce((sum, d) => sum + d.visitors, 0);
          return (
            <>
              <p className="-mt-3 mb-5 text-sm text-midnight/70">
                Counted by the website itself: no cookies, no outside services, visitors stay anonymous. Kept for 12 months.
                {r.since && ` Data since ${r.since}.`}
              </p>
              <StatsFilters filters={filters} options={r.options} />
              <p className="mt-4 mb-4 text-sm text-midnight/70">
                {filters.from === filters.to ? filters.from : `${filters.from} to ${filters.to}`} · compared with {r.previous.from} to {r.previous.to}
              </p>
              <Kpis kpis={r.kpis} />
              <div className="mt-4 grid gap-4 lg:grid-cols-2">
                <Panel title="Visits over time" wide>
                  {r.empty ? <Empty>No visits in this period yet.</Empty> : <Chart buckets={r.buckets} unit={r.unit} />}
                </Panel>
                <Panel title="Top pages" hint="Click a page to see only its numbers." wide>
                  <RankTable
                    head={["Page", "Views", "Visitors", "Time"]}
                    rows={r.pages.map((p) => ({ key: p.path, label: p.title, href: filterHref(filters, { page: p.path }), value: p.views, cells: [p.views, p.visitors, fmtDuration(p.time)] }))}
                    limit={15}
                  />
                </Panel>
                <Panel title="Where visitors come from">
                  <RankTable head={["Source", "Visitors", "Share"]} rows={r.sources.map((s) => ({ key: s.key, label: s.key, href: filterHref(filters, { source: s.key }), value: s.visitors, cells: [s.visitors, share(s.visitors, totalVisitors)] }))} />
                </Panel>
                <Panel title="Regions" hint="From each visitor's time zone.">
                  <RankTable head={["Region", "Visitors", "Share"]} rows={r.regions.map((s) => ({ key: s.key, label: s.key, href: filterHref(filters, { region: s.key }), value: s.visitors, cells: [s.visitors, share(s.visitors, totalVisitors)] }))} />
                </Panel>
                <Panel title="Devices">
                  <RankTable
                    head={["Device", "Visitors", "Share"]}
                    rows={r.devices.map((s) => ({ key: s.key, label: s.key[0].toUpperCase() + s.key.slice(1), href: filterHref(filters, { device: s.key }), value: s.visitors, cells: [s.visitors, share(s.visitors, totalVisitors)] }))}
                  />
                </Panel>
                <Panel title="Leads" hint="Contact form messages in this period.">
                  {r.leads.total ? (
                    <>
                      <RankTable head={["Budget", "Leads"]} rows={r.leads.byBudget.map(([k, v]) => ({ key: k, label: k, value: v, cells: [v] }))} />
                      <ul className="mt-4 flex flex-col gap-2 text-sm">
                        {r.leads.list.map((l) => (
                          <li key={l.id} className="flex flex-wrap justify-between gap-2 border-t border-midnight/10 pt-2">
                            <span className="font-bold">{l.name}</span>
                            <span className="text-midnight/70">
                              {l.source} · {l.region}
                            </span>
                          </li>
                        ))}
                      </ul>
                      <Link href="/admin/leads" className="mt-3 inline-block py-2 text-sm text-teal underline underline-offset-4">
                        Open the inbox →
                      </Link>
                    </>
                  ) : (
                    <Empty>No leads in this period.</Empty>
                  )}
                </Panel>
                <Panel title="How far articles are read" hint="Share of each article's visitors who reached 25%, 50%, 75% and the end." wide>
                  <RankTable
                    head={["Article", "Visitors", "25%", "50%", "75%", "End"]}
                    rows={r.articles.map((a) => ({ key: a.path, label: a.title, href: filterHref(filters, { page: a.path }), value: a.visitors, cells: [a.visitors, `${a.r25}%`, `${a.r50}%`, `${a.r75}%`, `${a.r100}%`] }))}
                  />
                </Panel>
                <Panel title="Clicks" hint="Calls to action people used.">
                  <RankTable head={["Button or link", "Clicks"]} rows={r.clicks.map(([k, v]) => ({ key: k, label: CLICK_NAMES[k] ?? k, value: v, cells: [v] }))} />
                </Panel>
                <Panel title="The character" hint="Playing with The Watching A.">
                  <RankTable head={["Interaction", "Times"]} rows={r.character.map(([k, v]) => ({ key: k, label: characterName(k), value: v, cells: [v] }))} />
                </Panel>
                <Panel title="Page speed" hint="What real visitors experienced (the 75th percentile, Google's standard).">
                  <Vitals vitals={r.vitals} />
                </Panel>
                <Panel title="Broken links" hint="Addresses people tried that don't exist. Fix the link that sent them, or tell us to add a redirect.">
                  <RankTable head={["Address", "Times"]} rows={r.notFound.map(([k, v]) => ({ key: k, label: k, value: v, cells: [v] }))} />
                </Panel>
                <Panel title="Content health" hint="Things to improve, from the website's own content." wide>
                  {r.health.length ? (
                    <ul className="flex flex-col gap-2 text-sm">
                      {r.health.map((h, i) => (
                        <li key={i} className="flex flex-wrap items-baseline gap-x-3 gap-y-1 border-t border-midnight/10 pt-2 first:border-0 first:pt-0">
                          <span className="rounded-full bg-midnight/10 px-2 py-0.5 font-mono text-[0.65rem] tracking-[0.1em] uppercase">{h.kind}</span>
                          <Link href={h.href} className="flex-1 py-1 hover:text-teal">
                            {h.text}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <Empty>Everything looks good.</Empty>
                  )}
                </Panel>
              </div>
            </>
          );
        }}
      </Loaded>
    </div>
  );
}

// ---------- lists and editors ----------

const hasArabicPost = (post: Pick<PostRecord, "ar">) => Boolean(post.ar?.title?.trim() && post.ar.summary?.trim() && post.ar.body?.length);

export function BlogPage() {
  const state = useLoad(() => collection<PostRecord[]>("posts"), "posts");
  return (
    <div>
      <PageTitle title="Blog">
        <Link href="/admin/blog/new" className={BTN_PRIMARY}>
          + New post
        </Link>
      </PageTitle>
      <Loaded state={state}>
        {(posts) => (
          <SortableList
            rows={posts.map((p) => ({ id: p.id, title: p.title, subtitle: `${p.category} · ${p.published}${hasArabicPost(p) ? " · English & Arabic" : " · No Arabic version yet"}`, status: p.status, editHref: `/admin/blog/${p.id}`, viewHref: `/blog/${p.slug}` }))}
            reorder={reorderPosts}
            remove={deletePost}
            empty="No posts yet. Write the first one."
          />
        )}
      </Loaded>
    </div>
  );
}

export function PostPage() {
  const { id = "new" } = useParams();
  const state = useLoad(async () => {
    const [posts, media] = await Promise.all([id === "new" ? [] : collection<PostRecord[]>("posts"), collection<MediaItem[]>("media").catch(() => [])]);
    return { post: id === "new" ? null : (posts.find((p) => p.id === id) ?? ("missing" as const)), media };
  }, `post:${id}`);
  return <Loaded state={state}>{({ post, media }) => (post === "missing" ? <NotFound what="article" /> : <PostEditor key={id} post={post} media={media} />)}</Loaded>;
}

export function ProjectsPage() {
  const state = useLoad(() => collection<ProjectRecord[]>("projects"), "projects");
  return (
    <div>
      <PageTitle title="Projects">
        <Link href="/admin/projects/new" className={BTN_PRIMARY}>
          + New project
        </Link>
      </PageTitle>
      <Loaded state={state}>
        {(projects) => (
          <SortableList
            rows={projects.map((p) => ({ id: p.id, title: p.name, subtitle: [p.client, p.tags.join(" · "), !p.ar?.name?.trim() && "Arabic name missing"].filter(Boolean).join(" — "), status: p.status, editHref: `/admin/projects/${p.id}`, viewHref: "/projects" }))}
            reorder={reorderProjects}
            remove={deleteProject}
            empty="No projects yet. Add the first one."
          />
        )}
      </Loaded>
    </div>
  );
}

export function ProjectPage() {
  const { id = "new" } = useParams();
  const state = useLoad(async () => {
    const [projects, media] = await Promise.all([id === "new" ? [] : collection<ProjectRecord[]>("projects"), collection<MediaItem[]>("media").catch(() => [])]);
    return { project: id === "new" ? null : (projects.find((p) => p.id === id) ?? ("missing" as const)), media };
  }, `project:${id}`);
  return <Loaded state={state}>{({ project, media }) => (project === "missing" ? <NotFound what="project" /> : <ProjectEditor key={id} project={project} media={media} />)}</Loaded>;
}

export function ServicesPage() {
  const state = useLoad(() => collection<ServiceRecord[]>("services"), "services");
  return (
    <div>
      <PageTitle title="Services">
        <Link href="/admin/services/new" className={BTN_PRIMARY}>
          + New service
        </Link>
      </PageTitle>
      <Loaded state={state}>
        {(services) => (
          <SortableList
            rows={services.map((s) => ({ id: s.id, title: s.name, subtitle: s.ar?.name?.trim() && s.ar.intro?.trim() ? s.summary : `${s.summary} · Arabic text missing`, status: s.status, editHref: `/admin/services/${s.id}`, viewHref: `/services/${s.slug}` }))}
            reorder={reorderServices}
            remove={deleteService}
            empty="No services yet. Add the first one."
          />
        )}
      </Loaded>
    </div>
  );
}

export function ServicePage() {
  const { id = "new" } = useParams();
  const state = useLoad(async () => {
    const services = id === "new" ? [] : await collection<ServiceRecord[]>("services");
    return { service: id === "new" ? null : (services.find((s) => s.id === id) ?? ("missing" as const)) };
  }, `service:${id}`);
  return <Loaded state={state}>{({ service }) => (service === "missing" ? <NotFound what="service" /> : <ServiceEditor key={id} service={service} />)}</Loaded>;
}

export function FaqPage() {
  const state = useLoad(() => collection<FaqGroupRecord[]>("faq"), "faq");
  return <Loaded state={state}>{(groups) => <FaqEditor groups={groups} />}</Loaded>;
}

export function TextPage() {
  const state = useLoad(() => collection<Pages>("pages"), "pages");
  return <Loaded state={state}>{(pages) => <PagesEditor pages={pages} part="pages" />}</Loaded>;
}

export function InterfacePage() {
  const state = useLoad(async () => {
    const ui = await collection<{ overrides: MessageOverrides; defaults: { en: Record<string, string>; ar: Record<string, string> } }>("ui");
    setDefaultMessages(ui.defaults);
    return ui.overrides;
  }, "ui");
  return <Loaded state={state}>{(overrides) => <InterfaceTextEditor overrides={{ en: overrides?.en ?? {}, ar: overrides?.ar ?? {} }} />}</Loaded>;
}

export function SettingsPage() {
  const state = useLoad(async () => {
    const [pages, launch] = await Promise.all([collection<Pages>("pages"), collection<{ comingSoon?: boolean }>("launch")]);
    return { pages, launch };
  }, "settings");
  return <Loaded state={state}>{({ pages, launch }) => <PagesEditor pages={pages} part="settings" top={<LaunchSwitch on={launch.comingSoon !== false} />} />}</Loaded>;
}

export function MediaPage() {
  const state = useLoad(() => collection<MediaItem[]>("media"), "media");
  return <Loaded state={state}>{(media) => <MediaLibrary media={media} />}</Loaded>;
}

export function LeadsPage() {
  const state = useLoad(async () => (await site().get<{ leads: LeadRecord[] }>("/leads")).leads, "leads");
  return <Loaded state={state}>{(leads) => <LeadsInbox leads={leads} />}</Loaded>;
}

export function ScoresPage() {
  const state = useLoad(async () => (await site().get<{ scores: ScoreRecord[] }>("/scores?limit=300")).scores, "scores");
  return <Loaded state={state}>{(scores) => <ScoresAdmin scores={scores} />}</Loaded>;
}

export function HistoryPage() {
  const state = useLoad(async () => (await site().get<{ versions: VersionInfo[] }>("/history/posts")).versions, "history");
  return <Loaded state={state}>{(versions) => <HistoryBrowser initialPart="posts" initialVersions={versions} />}</Loaded>;
}

export function PublishPage() {
  return <PublishPanel />;
}

function NotFound({ what }: { what: string }) {
  return (
    <div className={CARD}>
      <h1 className="text-2xl font-bold">This {what} no longer exists</h1>
      <p className="mt-2 text-midnight/70">Someone may have deleted it.</p>
    </div>
  );
}
